import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import Hls from 'hls.js';
import {
  Maximize,
  Pause,
  Play,
  FastForward,
  Sparkles,
} from 'lucide-react';
import styled, { useTheme, keyframes, css } from 'styled-components';
import { Container } from '../../layouts';

export interface ReactionOption {
  icon: React.ReactNode;
  value: string;
}

export interface EliMediaPlayerProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  mediaName: string;
  baseUrl: string;
  videoExt?: 'mp4' | 'webm' | 'm3u8';
  thumbnailExt?: 'jpg' | 'png' | 'webp';
  enableFullscreen?: boolean;
  forceWatchSpeed?: number;
  disableSeek?: boolean;
  hideNativeControls?: boolean;
  autoHideControls?: boolean;
  onMilestoneProgress?: (percent: number) => void;
  onVideoComplete?: () => void;
  onAnalyticsEvent?: (event: string, data?: any) => void;
  onWatchTimeUpdate?: (secondsWatched: number, percent: number) => void;
  reactions?: ReactionOption[];
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  forceMobile?: boolean;
  showXPNotifications?: boolean;
  showProgressOverlay?: boolean;
  allowOverflow?: boolean;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatUp = keyframes`
  0% { opacity: 0; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(-20px); }
  100% { opacity: 0; transform: translateY(-40px); }
`;

const ControlButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors?.textPrimary || 'white'};
  display: flex;
  align-items: center;
`;

const SpeedLabel = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors?.warning || '#facc15'};
`;

const XPBadge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: ${({ theme }) => theme.colors?.success || '#66BB6A'};
  color: white;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  z-index: 3;
  ${css`animation: ${fadeIn} 0.5s ease-in-out;`}
`;

const ReactionBurst = styled.div`
  position: absolute;
  animation: ${floatUp} 1s ease forwards;
  pointer-events: none;
  font-size: 1.25rem;
`;

const ReactionPanel = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 3;
`;

const ReactionButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  padding: 6px;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.2s ease;
  position: relative;
  &:hover {
    transform: scale(1.15);
  }
`;

const AnimatedControls = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  pointer-events: none;
  z-index: 2;
  ${css`animation: ${fadeIn} 0.3s ease-in-out;`}
`;


const ProgressOverlay = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.85);
  color: ${({ theme }) => theme.colors?.textPrimary || '#1A1A1A'};
  font-size: 0.75rem;
  padding: 4px 10px;
  border-radius: 12px;
  z-index: 3;
  ${css`animation: ${fadeIn} 0.3s ease-in-out;`}
`;

const MediaWrapper = styled.div<{ allowOverflow?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: ${({ allowOverflow }) => (allowOverflow ? 'visible' : 'hidden')};
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
  inset: 0;
  z-index: 0;
  display: block;
`;

export interface EliMediaPlayerHandle {
    play: () => void;
    pause: () => void;
    seekTo: (seconds: number) => void;
    getDuration?: () => number;
}

export const EliMediaPlayer = forwardRef<EliMediaPlayerHandle, EliMediaPlayerProps>(({
    mediaName,
    baseUrl,
    forceMobile = false,
    videoExt = 'mp4',
    thumbnailExt = 'jpg',
    enableFullscreen = false,
    forceWatchSpeed,
    disableSeek = false,
    hideNativeControls = false,
    autoHideControls = true,
    onMilestoneProgress,
    onVideoComplete,
    onAnalyticsEvent,
    onWatchTimeUpdate,
    reactions = [],
    autoPlay = false,
    loop = false,
    muted = false,
    playsInline = true,
    showXPNotifications = false,
    showProgressOverlay=false,
    allowOverflow=false,
    ...rest
  }, ref) => {
  
  const [percentWatched, setPercentWatched] = useState<number>(0);
  const [videoUrl, setVideoUrl] = useState('');
  const [posterUrl, setPosterUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [xpMessage, setXpMessage] = useState<string | null>(null);
  const [isMobileVertical, setIsMobileVertical] = useState(forceMobile || isMobile);
  const [reactionBursts, setReactionBursts] = useState<any[]>([]);
  const theme = useTheme();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastReportedSeconds = useRef<number>(0);
  const progressReported = useRef<Set<number>>(new Set());

  useImperativeHandle(ref, () => ({
    play: () => videoRef.current?.play(),
    pause: () => videoRef.current?.pause(),
    seekTo: (seconds: number) => {
      if (videoRef.current) videoRef.current.currentTime = seconds;
    },
    getDuration: () => {
      return videoRef.current?.duration || 0;
    },
  }));
  
  const updateMediaPaths = () => {
    const variant = forceMobile ? 'mobile' : isMobile ? 'mobile' : 'optimized';
    const name = isMobileVertical ? `${mediaName}-vertical` : mediaName;
    setVideoUrl(`${baseUrl}/videos/${variant}/${name}.${videoExt}`);
    setPosterUrl(`${baseUrl}/videos/thumbnails/${variant}/${name}.${thumbnailExt}`);
  };

  useEffect(updateMediaPaths, [mediaName, baseUrl, videoExt, thumbnailExt, isMobileVertical]);

  useEffect(() => {
    const onOrientationChange = () => {
      const angle = window.screen.orientation?.angle || 0;
      setIsMobileVertical(angle === 0 || angle === 180);
    };
    window.addEventListener('orientationchange', onOrientationChange);
    return () => window.removeEventListener('orientationchange', onOrientationChange);
  }, []);
  useEffect(() => {
    if (videoExt === 'm3u8' && videoRef.current && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(videoUrl);
      hls.attachMedia(videoRef.current);
      return () => hls.destroy();
    }
  }, [videoUrl, videoExt]);

  useEffect(() => {
    if (forceWatchSpeed && videoRef.current) {
      videoRef.current.playbackRate = forceWatchSpeed;
    }
  }, [forceWatchSpeed]);

  const toggleFullScreen = () => {
    const video = videoRef.current;
    if (!video) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen().catch(() => {});
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;

    const current = video.currentTime || 1; // prevent division by 0
    
    const duration = video.duration || 1; // prevent division by 0
    
    if (disableSeek && current > lastReportedSeconds.current + 1 && current < duration - 0.5) {
      video.currentTime = lastReportedSeconds.current;
      return;
    }

    const secondsWatched = Math.floor(current);
    // let percent = Math.floor((current / duration) * 100);
    let percent = Math.round((current / duration) * 100);
    if (duration - current < 0.3) {
      percent = 100;
    }

    if (secondsWatched > lastReportedSeconds.current) {
      lastReportedSeconds.current = secondsWatched;
      onWatchTimeUpdate?.(secondsWatched, percent);
      onMilestoneProgress?.(percent); // ✅ Call the custom handler
      // setPercentWatched(percent); // ✅ for overlay


    }
    setPercentWatched(percent); // ✅ for overlay
    
    const thresholds = [10, 25, 50, 75, 100];
    thresholds.forEach((t) => {
      if (percent >= t && !progressReported.current.has(t)) {
        onAnalyticsEvent?.('progress', { mediaName, percent });
        progressReported.current.add(t);
        setXpMessage(`+${t} XP!`);
        setTimeout(() => setXpMessage(null), 2000);
      }
    });

  };

  const handleMouseMove = () => {
    if (!autoHideControls) return;
    setShowControls(true);
    setTimeout(() => setShowControls(false), 2000);
  };

  const handleClick = () => {
    if (isMobile && autoHideControls) {
      setShowControls((prev) => !prev);
    }
  };

  const handleReaction = (reaction: string) => {
    onAnalyticsEvent?.('reaction', { mediaName, reaction });
    const id = Math.random().toString(36);
    setReactionBursts((prev) => [...prev, { id, reaction }]);
    setTimeout(() => {
      setReactionBursts((prev) => prev.filter((b) => b.id !== id));
    }, 1000);
  };

  return (
    <MediaWrapper
      allowOverflow={allowOverflow}
    //  style={{ position: 'relative', width: '100%' }} 
     onMouseMove={handleMouseMove} onClick={handleClick}>
      <StyledVideo
        ref={videoRef}
        poster={posterUrl}
        //  style={{ width: '100%', height: 'auto', display: 'block', zIndex: 1 }}
        controls={!hideNativeControls}
        onEnded={() => {
          if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = videoRef.current.duration;
          }
          onAnalyticsEvent?.('video_complete');
          onVideoComplete?.();
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        {...rest}
      >
        {videoExt !== 'm3u8' && <source src={videoUrl} type={`video/${videoExt}`} />}
        Your browser does not support the video tag.
      </StyledVideo>

      {showXPNotifications !== false && xpMessage && (
  <XPBadge>
    <Sparkles size={16} /> {xpMessage}
  </XPBadge>
  
)}
{showProgressOverlay && (
    <ProgressOverlay>Progress: {Math.round(percentWatched)}%</ProgressOverlay>
  )}
      {showControls && (
        <AnimatedControls>
          <div style={{ pointerEvents: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <ControlButton onClick={togglePlay}>
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </ControlButton>
            {enableFullscreen && (
              <ControlButton onClick={toggleFullScreen}>
                <Maximize size={24} />
              </ControlButton>
            )}
            {forceWatchSpeed && (
              <SpeedLabel>
                <FastForward size={16} /> {forceWatchSpeed}x
              </SpeedLabel>
            )}
          </div>
        </AnimatedControls>
      )}

      {reactions.length > 0 && (
        <ReactionPanel>
          {reactions.map((reaction) => (
            <ReactionButton key={reaction.value} onClick={() => handleReaction(reaction.value)}>
              {reaction.icon}
              {reactionBursts.map((b) =>
                b.reaction === reaction.value ? (
                  <ReactionBurst key={b.id}>{reaction.icon}</ReactionBurst>
                ) : null
              )}
            </ReactionButton>
          ))}
        </ReactionPanel>
      )}
    </MediaWrapper>
  );
});

